import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ModelFolderSchema, type Download, type ModelFolder } from '@couch/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, api, errorMessage } from '../../api/client';
import { formatBytes, percent } from './format';

export const downloadsKey = (id: string) => ['downloads', id] as const;

function AddFromLink({ machineId }: { machineId: string }) {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState('');
  const [folder, setFolder] = useState<ModelFolder | ''>('');
  const start = useMutation({
    mutationFn: () => api.startDownload(machineId, folder ? { url, folder } : { url }),
    onSuccess: () => { setUrl(''); void queryClient.invalidateQueries({ queryKey: downloadsKey(machineId) }); },
  });
  const needsToken = start.error instanceof ApiError && start.error.code === 'needs_token';
  return (
    <form className="link-form" onSubmit={(e) => { e.preventDefault(); start.mutate(); }}>
      <div className="field">
        <label htmlFor="model-link">Hugging Face or Civitai link</label>
        <input id="model-link" value={url} onChange={(e) => { setUrl(e.target.value); }} placeholder="https://civitai.com/models/…" />
      </div>
      <div className="field">
        <label htmlFor="model-folder">Folder</label>
        <select id="model-folder" value={folder} onChange={(e) => { setFolder(e.target.value as ModelFolder | ''); }}>
          <option value="">Automatic</option>
          {ModelFolderSchema.options.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>
      <button type="submit" disabled={start.isPending || url.trim() === ''}>Download</button>
      {start.isError && (
        <p role="alert" className="form-error">
          <span>{errorMessage(start.error)}</span>{needsToken && <> — <Link to="/account">Model sources</Link></>}
        </p>
      )}
    </form>
  );
}

function DownloadRow({ machineId, download }: { machineId: string; download: Download }) {
  const queryClient = useQueryClient();
  const cancel = useMutation({ mutationFn: () => api.cancelDownload(machineId, download.id), onSuccess: () => queryClient.invalidateQueries({ queryKey: downloadsKey(machineId) }) });
  const pct = percent(download.receivedBytes, download.sizeBytes);
  return (
    <li className="download-row">
      <span className="download-name">{download.fileName}</span>
      <span className="muted">{download.folder} · {formatBytes(download.receivedBytes)} of {formatBytes(download.sizeBytes)} · {download.status}</span>
      {download.status === 'running' && (
        <>
          <div role="progressbar" aria-label={`Download ${download.fileName}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct ?? undefined} className="progress">
            <span style={{ width: `${pct ?? 0}%` }} />
          </div>
          <button type="button" className="button-quiet" aria-label={`Cancel ${download.fileName}`} onClick={() => { cancel.mutate(); }}>Cancel</button>
        </>
      )}
      {download.error && <span className="form-error">{download.error}</span>}
    </li>
  );
}

export function DownloadsPanel({ machineId }: { machineId: string }) {
  const queryClient = useQueryClient();
  const downloads = useQuery({
    queryKey: downloadsKey(machineId), queryFn: () => api.listDownloads(machineId), retry: false,
    refetchInterval: (query) => (query.state.data?.downloads.some((d) => d.status === 'running') ? 1500 : 15_000),
  });
  const running = downloads.data?.downloads.filter((d) => d.status === 'running').length ?? 0;
  const previous = useRef(running);
  useEffect(() => {
    if (running < previous.current) void queryClient.invalidateQueries({ queryKey: ['models', machineId] });
    previous.current = running;
  }, [running, machineId, queryClient]);
  return (
    <section className="panel" aria-labelledby="downloads-title">
      <h2 id="downloads-title">Add a model</h2>
      <AddFromLink machineId={machineId} />
      {downloads.isError && <p role="alert" className="form-error">{errorMessage(downloads.error)}</p>}
      {downloads.data && downloads.data.downloads.length > 0 && (
        <ul className="download-list">{downloads.data.downloads.map((d) => <DownloadRow key={d.id} machineId={machineId} download={d} />)}</ul>
      )}
    </section>
  );
}
